import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Folder, Trash2, FileBox, X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ModelBrowser = ({
  isOpen,
  onClose,
  models,
  samples,
  onSelectModel,
  onUpload,
  onDelete,
  isLoading
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.gltf') || file.name.endsWith('.glb')) {
        setUploading(true);
        await onUpload(file);
        setUploading(false);
      }
    }
  }, [onUpload]);
  
  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      await onUpload(file);
      setUploading(false);
    }
  }, [onUpload]);
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="model-browser">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-primary" />
            Model Browser
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Upload area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/10' : 'border-border'
            }`}
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Drag & drop a .gltf or .glb file here
            </p>
            <label>
              <input
                type="file"
                accept=".gltf,.glb"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <Button variant="outline" size="sm" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Browse Files'}
              </Button>
            </label>
          </div>
          
          {/* Sample models */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Sample Models</h4>
            <div className="grid grid-cols-3 gap-2">
              {samples.map((sample) => (
                <button
                  key={sample.id}
                  onClick={() => onSelectModel(sample)}
                  className="p-3 bg-secondary/50 hover:bg-secondary rounded-lg text-left transition-colors"
                  data-testid={`sample-${sample.id}`}
                >
                  <FileBox className="w-6 h-6 text-primary mb-2" />
                  <div className="text-sm font-medium truncate">{sample.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{sample.description}</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Uploaded models */}
          {models.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Uploaded Models</h4>
              <div className="grid grid-cols-3 gap-2">
                {models.map((model) => (
                  <div
                    key={model.id}
                    className="p-3 bg-secondary/50 rounded-lg group relative"
                  >
                    <button
                      onClick={() => onSelectModel(model)}
                      className="w-full text-left"
                    >
                      <FileBox className="w-6 h-6 text-accent mb-2" />
                      <div className="text-sm font-medium truncate">{model.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(model.file_size / 1024).toFixed(1)} KB
                      </div>
                    </button>
                    <button
                      onClick={() => onDelete(model.id)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-destructive/20 hover:bg-destructive/30 rounded transition-opacity"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModelBrowser;
